import { useState, useContext } from 'react'
import { Table } from 'antd'
import { TEACHER } from '@BUP/constants'
import { useTranslation } from 'react-i18next'
import styles from './teacher_page.module.scss'
import PageLayout from '@BUP/components/Layout/Page'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { useGetEnrollmentsByRoleType } from '@BUP/hooks/Enrollments/useGetEnrollmentsByRoleType'
import { useGetRegistrations } from '@BUP/hooks/registration/useGetRegistrations'
import { useGetCurrentTerm } from '@BUP/hooks/Terms/useGetCurrentTerm'
import { useResetPassword } from '@BUP/hooks/user/useResetPassword'
import { useGetCoursesByTermId } from '@BUP/hooks/Courses/useGetCoursesByTermId'
import { getTeacherTableColumns } from './teacher-table-columns.jsx'
import AddTeacherButton from './add-teacher-button'
import BupSpinner from '@BUP/components/Loading/Spinner'

const TeacherListPage = () => {
  const organization = useContext(OrganizationContext)
  const { type: organizationType } = organization
  const isMultiCourse = organizationType === 'multi-course'
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const {
    data: teachers,
    totalCount: teachersTotalCount,
    loading: loadingTeachers,
    fetching: fetchingTeachers
  } = useGetEnrollmentsByRoleType(organization.id, TEACHER, {
    page: currentPage,
    limit: pageSize
  })
  const { registrations, isLoadingRegistrations, isFetchingRegistrations } =
  useGetRegistrations('course:role:main-teacher')
  const { data: term } = useGetCurrentTerm()
  const { onPasswordReset } = useResetPassword()
  const i18n = useTranslation()
  const termId = term && term.id
  const { data: courses = [], isLoading: loadingCourses, isFetching: fetchingCourses } = useGetCoursesByTermId(termId)

  const columns = getTeacherTableColumns({
    i18n,
    courses,
    isMultiCourse,
    onPasswordReset
  })

  if (isLoadingRegistrations || loadingTeachers || loadingCourses) return <BupSpinner size='large' />

  const groupBy = (data, key) => {
    return data.reduce((result, item) => {
      const groupKey = item[key]
      result[groupKey] = result[groupKey] || []
      result[groupKey].push(item)
      return result
    }, {})
  }

  const groupCourses = groupBy(courses, 'id')
  const dataTeacherList = (teachers ?? []).map(teacher => {
    const enrollmentWithCourse = registrations.find(
      registration => registration.enrollmentId === teacher.id
    )

    const course = groupCourses[enrollmentWithCourse?.courseId]
    return {
      key: teacher.id,
      enrollmentId: teacher.id,
      name: teacher.user.name,
      email: teacher.user.email,
      mainTeacher: teacher.main_teacher,
      hasCourse: !!enrollmentWithCourse,
      courseId: course ? course[0].id : null,
      course: course ? course[0].name : '-',
      registrationId: enrollmentWithCourse?.id || null
    }
  }).sort((teacherA, teacherB) => {
    return Number(teacherB.hasCourse) - Number(teacherA.hasCourse)
  })

  const handleTeachersPaginationChange = (page, nextPageSize) => {
    setPageSize(nextPageSize)
    setCurrentPage(nextPageSize !== pageSize ? 1 : page)
  }

  const totalTeachers = teachersTotalCount ?? dataTeacherList.length

  return (
    <PageLayout
      pageName={i18n.t('common.resources.role.values.type.teacher', { count: 2 })}
      backButton
    >
      <div className={styles.container}>
        <div className={styles.create_user_button_wrapper}>
          <AddTeacherButton courses={courses} isMultiCourse={isMultiCourse} />
        </div>
        <Table
          loading={isFetchingRegistrations || fetchingTeachers || fetchingCourses}
          columns={columns}
          dataSource={dataTeacherList}
          bordered
          pagination={{
            position: ['none', 'bottomCenter'],
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', totalTeachers],
            total: totalTeachers,
            onChange: handleTeachersPaginationChange
          }}
          className={styles.table}
        />
      </div>
    </PageLayout>
  )
}

export default TeacherListPage
